import { styled } from '@linaria/react';
import { Alert, Autocomplete, TextField } from '@mui/material';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { listOptions } from '../../../core/mapV2/edit';
import {
    LIST_FILTER_FIELDS,
    ListFilterField,
    NO_VALUE,
    RANGE_FILTER_FIELDS,
    RangeFilterField,
    hasUsableTableFilter,
} from '../../../core/mapV2/filters';
import { filtersQuery } from '../../../core/mapV2/queries';
import { filterListSet, filterRangeSet } from '../../../core/mapV2/state';
import { roadSectionsQuery } from '../../../core/reference/queries';
import theme from '../../../design/theme';
import { useAppDispatch, useAppSelector } from '../../../types';

const Panel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    box-sizing: border-box;
    width: 380px;
    padding: 16px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${theme.color.grey.light};
`;

const Range = styled.div`
    display: flex;
    gap: 8px;
`;

const parseBound = (text: string): number | null => {
    const value = Number(text.replace(',', '.'));

    return text.trim() === '' || Number.isNaN(value) ? null : value;
};

const ListFilter: FC<{ field: ListFilterField }> = ({ field }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const sections = useAppSelector(roadSectionsQuery);
    const selected = useAppSelector(filtersQuery).lists[field] ?? [];
    const options = useMemo(() => [NO_VALUE, ...listOptions(sections, field)], [sections, field]);

    return (
        <Autocomplete
            multiple
            size="small"
            options={options}
            value={selected}
            getOptionLabel={(value) => (value === NO_VALUE ? t('mapV2.filters.none') : value)}
            onChange={(_, values) => dispatch(filterListSet({ field, values }))}
            renderInput={(params) => <TextField {...params} label={t(`mapV2.sheet.${field}`)} />}
        />
    );
};

const RangeFilterInput: FC<{ field: RangeFilterField }> = ({ field }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const range = useAppSelector(filtersQuery).ranges[field];
    const set = (bound: 'min' | 'max', text: string) =>
        dispatch(
            filterRangeSet({
                field,
                min: bound === 'min' ? parseBound(text) : range?.min ?? null,
                max: bound === 'max' ? parseBound(text) : range?.max ?? null,
            }),
        );

    return (
        <Range>
            {(['min', 'max'] as const).map((bound) => (
                <TextField
                    key={bound}
                    size="small"
                    type="number"
                    fullWidth
                    label={`${t(`mapV2.sheet.${field}`)} ${t(`mapV2.filters.${bound}`).toLowerCase()}${
                        field === 'width' ? ' (m)' : ''
                    }`}
                    value={range?.[bound] ?? ''}
                    onChange={(event) => set(bound, event.target.value)}
                    inputProps={{ step: field === 'width' ? 0.1 : 1 }}
                />
            ))}
        </Range>
    );
};

// Criteria of the référentiel filters, opened from the KPI bar. Each change applies at once: sections that do
// not match are dimmed on the map and left out of the figures.
export const FiltersPanel: FC = () => {
    const { t } = useTranslation();
    const applyTable = useAppSelector((state) => state.mapV2.applyTableFilters.reference);
    const tableFilter = useAppSelector((state) => state.reference.filter);

    return (
        <Panel data-testid="map-v2-filters-panel">
            <Title>{t('mapV2.filters.title')}</Title>
            {LIST_FILTER_FIELDS.map((field) => (
                <ListFilter key={field} field={field} />
            ))}
            {RANGE_FILTER_FIELDS.map((field) => (
                <RangeFilterInput key={field} field={field} />
            ))}
            {applyTable && hasUsableTableFilter(tableFilter) ? (
                <Alert severity="info">{t('mapV2.filters.tableApplied')}</Alert>
            ) : null}
        </Panel>
    );
};
